import type { TranslationKey } from '@/i18n/translations'

/**
 * Real pricing/logic ported EXACTLY from the live site's calculator
 * (index.html's `recalc()` IIFE, the `BUNDLEABLE` array, `wdRecalc()`, and
 * `GRAFIKA_ITEMS` — read directly from source this session, not
 * approximated, per the master prompt's explicit instruction on the
 * calculator). See app/handoff.md §4 for the full read-through notes.
 *
 * One resolved discrepancy: the live site's language add-on copy used to
 * say "+50 EUR po jeziku" while the actual `recalc()` charged 100 EUR per
 * language — confirmed with the user, 100 EUR/language is correct. The
 * live site's *actual* rendered copy (from the real `translations` JS
 * dictionary that `applyLang()` reads, not the pre-JS static HTML
 * fallback) already doesn't state a number in the addon note itself, so
 * there's nothing to reconcile in the copy.
 */

export const BASE_PRICE = 200
export const TIER_PRO_THRESHOLD = 440
export const TIER_PREMIUM_THRESHOLD = 775
export const LANGUAGE_PRICE_PER_LANG = 100

export type TierKey = 'basic' | 'pro' | 'premium'

/** A bundleable add-on for the main "new website" calculator. */
export interface Addon {
  id: string
  nameKey: TranslationKey
  noteKey: TranslationKey
  /** Price when bundled with a new website purchase. */
  bundledPrice: number
  /** Price if bought standalone (shown struck-through next to the bundled price). */
  standalonePrice: number
  forcePremium?: boolean
}

export const ADDONS: Addon[] = [
  { id: 'gallery', nameKey: 'addon-gallery-name', noteKey: 'addon-gallery-note', bundledPrice: 40, standalonePrice: 50 },
  { id: 'contactform', nameKey: 'addon-contact-name', noteKey: 'addon-contact-note', bundledPrice: 25, standalonePrice: 35 },
  { id: 'maps', nameKey: 'addon-maps-name', noteKey: 'addon-maps-note', bundledPrice: 20, standalonePrice: 25 },
  { id: 'logo', nameKey: 'addon-logo-name', noteKey: 'addon-logo-note', bundledPrice: 40, standalonePrice: 50 },
  { id: 'copy', nameKey: 'addon-copy-name', noteKey: 'addon-copy-note', bundledPrice: 80, standalonePrice: 100 },
  { id: 'seo', nameKey: 'addon-seo-name', noteKey: 'addon-seo-note', bundledPrice: 60, standalonePrice: 80 },
  { id: 'calc', nameKey: 'addon-calc-name', noteKey: 'addon-calc-note', bundledPrice: 80, standalonePrice: 100 },
  { id: 'admin', nameKey: 'addon-admin-name', noteKey: 'addon-admin-note', bundledPrice: 145, standalonePrice: 180, forcePremium: true },
  { id: 'db', nameKey: 'addon-db-name', noteKey: 'addon-db-note', bundledPrice: 100, standalonePrice: 130, forcePremium: true },
]

/** Recurring monthly add-on — kept out of `ADDONS` since it has no standalone/bundle price pair and never counts toward the one-time total or tier. */
export const MAINTENANCE_ADDON = {
  id: 'maintenance',
  nameKey: 'addon-maint-name' as TranslationKey,
  noteKey: 'addon-maint-note' as TranslationKey,
  price: 20,
}

/** Existing-site ("Web design") calculator — flat pricing, no bundle discount, reuses addon names but has its own note copy. */
export interface WdAddon {
  id: string
  nameKey: TranslationKey
  noteKey: TranslationKey
  price: number
  recurring?: boolean
}

export const WD_ADDONS: WdAddon[] = [
  { id: 'wd-gallery', nameKey: 'addon-gallery-name', noteKey: 'wd-gallery-note', price: 100 },
  { id: 'wd-contactform', nameKey: 'addon-contact-name', noteKey: 'wd-contact-note', price: 35 },
  { id: 'wd-maps', nameKey: 'addon-maps-name', noteKey: 'wd-maps-note', price: 25 },
  { id: 'wd-copy', nameKey: 'addon-copy-name', noteKey: 'wd-copy-note', price: 100 },
  { id: 'wd-seo', nameKey: 'addon-seo-name', noteKey: 'wd-seo-note', price: 80 },
  { id: 'wd-calc', nameKey: 'addon-calc-name', noteKey: 'wd-calc-note', price: 100 },
  { id: 'wd-admin', nameKey: 'addon-admin-name', noteKey: 'wd-admin-note', price: 180 },
  { id: 'wd-db', nameKey: 'addon-db-name', noteKey: 'wd-db-note', price: 130 },
  { id: 'wd-maintenance', nameKey: 'addon-maint-name', noteKey: 'addon-maint-note', price: 20, recurring: true },
]

/** Graphic design multi-select items — literal bs/en copy inline, matching the live site's `GRAFIKA_ITEMS` shape exactly (not part of the shared translations dictionary there either). */
export interface GrafikaItem {
  id: string
  name: string
  nameEn: string
  price: number
  desc: string
  descEn: string
}

export const GRAFIKA_ITEMS: GrafikaItem[] = [
  { id: 'logo', name: 'Izrada logotipa', nameEn: 'Logo design', price: 50, desc: 'Moderan, jedinstven logo prilagođen za web, print i društvene mreže.', descEn: 'Modern, unique logo optimised for web, print and social media.' },
  { id: 'card', name: 'Vizit karta', nameEn: 'Business card', price: 25, desc: 'Profesionalna vizit karta za print — obostrana, CMYK format.', descEn: 'Professional print-ready business card — double-sided, CMYK.' },
  { id: 'poster', name: 'Poster / flyer', nameEn: 'Poster / flyer', price: 20, desc: 'Dizajn postera ili flyera za događaje, promocije ili oglašavanje.', descEn: 'Poster or flyer design for events, promotions or advertising.' },
  { id: 'banner', name: 'Statični oglas / banner', nameEn: 'Static ad / banner', price: 20, desc: 'Vizualno upečatljiv oglas za Facebook, Instagram ili Google.', descEn: 'Eye-catching ad banner for Facebook, Instagram or Google.' },
  { id: 'cover', name: 'Facebook / Instagram cover', nameEn: 'Facebook / Instagram cover', price: 15, desc: 'Naslovna fotografija za poslovnu stranicu — brendirana, ispravne dimenzije.', descEn: 'Branded cover photo for your business page — correct dimensions.' },
  { id: 'story', name: 'Story / reels grafika', nameEn: 'Story / reels graphic', price: 18, desc: 'Atraktivni vizual za Instagram / Facebook story ili reels format.', descEn: 'Attractive visual for Instagram / Facebook story or reels format.' },
  { id: 'road', name: 'Putna reklama', nameEn: 'Road signage', price: 30, desc: 'Dizajn reklamnih panoa i natpisa za pored puta — za veliku štampu.', descEn: 'Billboard and roadside sign design — ready for large-format print.' },
  { id: 'menu', name: 'Meni / cjenovnik', nameEn: 'Menu / price list', price: 30, desc: 'Dizajn menija ili cjenovnika — za print ili digitalni prikaz.', descEn: 'Menu or price list design — for print or digital display.' },
  { id: 'letterhead', name: 'Memorandum', nameEn: 'Letterhead', price: 15, desc: 'Službeni memorandum s logotipom i kontakt podacima za vašu firmu.', descEn: 'Official letterhead with your logo and contact details.' },
  { id: 'presentation', name: 'Prezentacija (PowerPoint/PDF)', nameEn: 'Presentation (PowerPoint/PDF)', price: 40, desc: 'Profesionalni dizajn prezentacije za poslovne svrhe.', descEn: 'Professional presentation design for business purposes.' },
  { id: 'packaging', name: 'Packaging / ambalaža mockup', nameEn: 'Packaging / mockup', price: 45, desc: 'Vizualni prikaz vašeg brenda na ambalaži ili packaging dizajnu.', descEn: 'Visual representation of your brand on packaging or product mockup.' },
]

export interface LanguageOption {
  id: 'en' | 'de'
  labelKey: TranslationKey
}

export const LANGUAGE_OPTIONS: LanguageOption[] = [
  { id: 'en', labelKey: 'lang-en' },
  { id: 'de', labelKey: 'lang-de' },
]

/** Computes the main calculator's tier from the one-time total + any forced-premium item, matching the live site's `recalc()` exactly. */
export function computeTier(oneTime: number, forcePremium: boolean): TierKey {
  if (forcePremium || oneTime >= TIER_PREMIUM_THRESHOLD) return 'premium'
  if (oneTime >= TIER_PRO_THRESHOLD) return 'pro'
  return 'basic'
}
